import { config } from "@/config";
import { ANALYTICS_EVENTS } from "@/configs/analyticsEvents";
import { removeUndefinedFromObject } from "@/lib/helpers";
import { IAdvizerVideo } from "@/models/AdvizerVideo";
import { User } from "@/models/User";
import { useStore } from "@/store";
import { Firestore } from "@/services/Firestore";
import mixpanel from "mixpanel-browser";
import { StoreService } from "./StoreService";

export type IAnalyticsMetrics = {
  timestamp?: number;
  videoWatchedSeconds?: number;
  videoDurationSeconds?: number;
  videoWatchedPercent?: number;
  videoSeekFromSeconds?: number;
  videoSeekToSeconds?: number;
  videoHasSeeked?: boolean;
  userSecondsWatched?: number;
};

export type IPIIEventData = {
  event: string;
  data: IAnalyticsMetrics;
};

const analyticsMetricsKeys: (keyof IAnalyticsMetrics)[] = [
  "timestamp",
  "videoWatchedSeconds",
  "videoDurationSeconds",
  "videoWatchedPercent",
  "videoSeekFromSeconds",
  "videoSeekToSeconds",
  "videoHasSeeked",
  "userSecondsWatched",
];

export type IAnalyticsEventData = IAnalyticsMetrics & {
  event?: string;
  action?: string;
  label?: string;
  // custom dimensions
  userAdvizeId?: string;
  userEmail?: string;
  userEmailDomain?: string;
  userPartnerId?: string;
  userPartnerSlug?: string;
  userPartner?: string;
  isLoggedIn?: boolean;

  email?: string;
  inputValue?: string;
  value?: number;
  error?: string;
  filterType?: string;
  filterValue?: string;
  resourceId?: string;
  context?: string;
  page?: string;
  url?: string;
  link?: string;

  partner?: string;
  partnerId?: string;
  partnerSlug?: string;
  advizerId?: string;
  advizerName?: string;

  // video dimensions
  videoId?: string;
  questionNumber?: number;
  videoTitle?: string;

  timestampMetric?: number;
  videoWatchedSecondsMetric?: number;
  videoDurationSecondsMetric?: number;
  videoWatchedPercentMetric?: number;
  videoSeekFromSecondsMetric?: number;
  videoSeekToSecondsMetric?: number;
  videoHasSeekedMetric?: boolean;
  userSecondsWatchedMetric?: number;

  // playlists
  isPlaylistVideo?: boolean;
  isPlaylistVideoString?: string;
  playlistId?: string;
  playlistTitle?: string;
  playlistStepKey?: string;
  playlistStepTitle?: string;
  playlistStepType?: string;
  isRecommendedPlaylist?: boolean;

  // assignments
  assignmentId?: string;
  assignmentType?: string;
  assignmentTitle?: string;
  assignmentPromptKey?: string;
  assignmentPrompt?: string;
  requiredCount?: number;
  isAssignmentVideo?: boolean;
  isRecommendedAdvizer?: boolean;
  // // METRICS
  // timestamp?: number;
  // videoWatchedSeconds?: number;
  // videoDurationSeconds?: number;
  // videoWatchedPercent?: number;
  // videoSeekFromSeconds?: number;
  // videoSeekToSeconds?: number;
  // videoHasSeeked?: boolean;
  // userSecondsWatched?: number;

  isPartnerApp?: boolean;
  isAlumniApp?: boolean;
  isDemoApp?: boolean;
  isFamilyApp?: boolean;

  // Errors
  stack?: string;
  componentStack?: string;
};

declare function gtag(
  command: string,
  event: string,
  data?: IAnalyticsEventData,
): void;

const debug = true;
class AnalyticsService {
  trackEvent(event: keyof typeof ANALYTICS_EVENTS, data?: IAnalyticsEventData) {
    try {
      const { activeUser: activeUserRaw } = useStore.getState().user;
      const activeUser = activeUserRaw ? new User(activeUserRaw) : null;
      const isLoggedIn = !!activeUser;
      const eventConfig = ANALYTICS_EVENTS[event];
      const email = data?.email || activeUser?.email;
      const activeUserPartner = useStore.getState().partner.activePartner;
      const isPartnerApp = config.isPartnerApp || false;
      const isAlumniApp = config.isAlumniApp || false;
      const isFamilyApp = config.isFamilyApp || false;
      const isDemoApp = config.isDemoApp || false;
      const hasRejectedCookies = activeUser?.rejectedCookies === true;
      const hasAcceptedCookies = activeUser?.acceptedCookies === true;

      if (!eventConfig) {
        console.error(`Event config not found for ${event}`);
        return;
      }

      if (eventConfig.disabled) {
        console.log("Event disabled: ", event);
        return;
      }

      const explicitlyTrackDemoApp =
        isDemoApp &&
        (config.isProductionClient ||
          config.isStagingClient ||
          config.isDemoProductionClient);

      const shouldBeAnonymous = eventConfig?.anonymous || hasRejectedCookies;

      const emailsToIgnore = [
        "yosttest",
        "ryost217",
        "rjyost",
        "ryanjyost",
        "emilymcsherry",
        "emilytest",
      ];

      // KEEP the original emailsToIgnore logic
      if (
        !config.isLocalClient &&
        emailsToIgnore.some((email) => activeUser?.email?.includes(email))
      ) {
        console.log("Untracked event - ignored email: ", {
          event,
          email: activeUser?.email,
        });
        return;
      }

      let eventParams: IAnalyticsEventData = {
        event,
        url: window.location.pathname,
        timestamp: Date.now(),
        isPartnerApp,
        isAlumniApp,
        isDemoApp,
        isLoggedIn,
        isFamilyApp,
      };

      // Only add user data if NOT anonymous and user has accepted cookies
      if (activeUser && !shouldBeAnonymous && hasAcceptedCookies) {
        eventParams.userAdvizeId = activeUser.uid;
        eventParams.userEmail = activeUser.email;

        // Only add user partner data for non-anonymous events
        if (activeUserPartner) {
          eventParams.userPartner = activeUserPartner.title;
          eventParams.userPartnerId = activeUserPartner.id;
          eventParams.userPartnerSlug = activeUserPartner.slug;
        }
      }

      // Handle email domain differently for anonymous events
      if (email && !shouldBeAnonymous && hasAcceptedCookies) {
        eventParams.userEmailDomain = email.split("@")[1];
      }

      // Add partner data from the event data (this is content context, not user data)
      if (data?.partnerId && !data.partner) {
        const partners = useStore.getState().advizeApiData.partners;
        const partner = partners?.find((p) => p.id === data.partnerId);
        eventParams.partner = partner?.title;
      }

      if (data?.partnerId && !data.partnerSlug) {
        const partners = useStore.getState().advizeApiData.partners;
        const partner = partners?.find((p) => p.id === data.partnerId);
        eventParams.partnerSlug = partner?.slug;
      }

      // SINGLE data addition with proper sanitization
      if (data) {
        eventParams = { ...eventParams, ...data };
      }

      // Add assignment context (this is content context, not user PII)
      // Run this AFTER data merge so we can resolve by explicit assignmentId from payload.
      try {
        if (event.startsWith("assignment_") || eventParams.assignmentId) {
          const assignmentParams = this.getAssignmentParams(
            eventParams.assignmentId,
          );
          eventParams.assignmentId =
            eventParams.assignmentId || assignmentParams.assignmentId;
          eventParams.assignmentTitle =
            eventParams.assignmentTitle || assignmentParams.assignmentTitle;
          eventParams.partnerSlug =
            eventParams.partnerSlug || assignmentParams.partnerSlug;
        }
      } catch (e) {
        console.error("Error getting assignment params", e);
      }

      // Add metric versions of analytics metrics
      analyticsMetricsKeys.forEach((metric) => {
        const value = eventParams[metric];
        if (
          typeof value !== "undefined" &&
          value !== null &&
          Number.isFinite(value)
        ) {
          (eventParams as any)[`${String(metric)}Metric`] = Number(value);
        }
      });

      try {
        eventParams = removeUndefinedFromObject(eventParams);
      } catch (e) {
        console.error("Error removing undefined from event params", e);
      }

      // NEW: Sanitize the final event object if it should be anonymous
      if (shouldBeAnonymous) {
        eventParams = this.sanitizeFinalEventObject(eventParams);
      }

      // Log what type of tracking we're doing
      if (shouldBeAnonymous && hasRejectedCookies) {
        console.log(`Tracking anonymous event: ${event}`, eventParams);
      }

      if (debug || config.isProductionClient) {
        console.log("EVENT: ", event, eventParams);
      }

      if (config.isProductionClient || explicitlyTrackDemoApp) {
        console.log("Saving analytics event", event, { eventParams });
        try {
          if (!eventConfig.excludeFromFirestore && config.isProductionClient) {
            Firestore.saveEvent({ event, data: eventParams });
          }
        } catch (e) {
          console.error("Error saving event to Firestore", e);
        }

        try {
          gtag("event", event, eventParams);
        } catch (e) {
          console.error("Error sending event to Google Analytics", e);
        }

        try {
          mixpanel.track(eventConfig?.title || event, eventParams);
        } catch (e) {
          console.error("Error sending event to Mixpanel", e);
        }
      }
    } catch (e) {
      console.error(`Error tracking event: ${event}`, e);
    }
  }

  private sanitizeFinalEventObject(
    eventParams: IAnalyticsEventData,
  ): IAnalyticsEventData {
    // Blacklist of fields that contain PII or user-identifying data
    const blacklistedFields: (keyof IAnalyticsEventData)[] = [
      // Direct user identification
      "userAdvizeId",
      "userEmail",
      "userEmailDomain",
      "userPartnerId",
      "userPartnerSlug",
      "userPartner",
      "email",
      "url", // Now handled here instead of conditionally adding it

      // Potentially identifying content
      "inputValue", // Could contain personal info typed by user
      "advizerName", // Could be identifying if it's a person's name

      // Error stack traces (could contain sensitive paths/data)
      "stack",
      "componentStack",

      // Assignment data that could link to specific users
      "assignmentPrompt", // Could contain personal assignment details
    ];

    // Create sanitized copy
    const sanitizedParams: any = {};

    Object.keys(eventParams).forEach((key) => {
      if (!blacklistedFields.includes(key as keyof IAnalyticsEventData)) {
        sanitizedParams[key] = eventParams[key as keyof IAnalyticsEventData];
      }
    });

    return sanitizedParams;
  }

  setAnonymousMode() {
    try {
      try {
        mixpanel.reset();
      } catch (e) {
        console.warn("Error resetting Mixpanel", e);
      }

      // Generate a truly session-only ID (not stored)
      const sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Use a non-persistent identification
      mixpanel.identify(sessionId);

      // Override persistence for anonymous users
      mixpanel.register(
        {
          anonymous_session: true,
        },
        { persistent: false },
      ); // This makes it session-only

      console.log("Mixpanel set to anonymous mode");
    } catch (e) {
      console.warn("Error setting anonymous mode:", e);
    }
  }

  identifyUser(user: User) {
    console.log("Identifying user", String(user.uid));
    try {
      mixpanel.identify(user.uid);
      mixpanel.people.set({
        $email: user.email,
        $name: user.displayName,
        user_id: user.uid,
        // Add other non-sensitive profile data
      });
      console.log("Mixpanel user identified");
    } catch (e) {
      console.warn("Error identifying user:", e);
    }
  }

  click({
    action,
    event,
    label,
    context,
    ...rest
  }: {
    action: string;
    event?: any;
    label?: string;
    context?: string;
  } & Omit<IAnalyticsEventData, "event">) {
    // event is native event here, not string
    this.trackEvent("click", {
      ...rest,
      action,
      label:
        label || event?.target?.innerText || event?.target?.textContent || null,
      context,
    });
  }

  invalidEmail(data: IAnalyticsEventData) {
    this.trackEvent("invalid_email", { ...data });
  }

  loginAttempt(data: IAnalyticsEventData) {
    this.trackEvent("login_attempt", { ...data });
  }

  login(data: IAnalyticsEventData) {
    this.trackEvent("login", { ...data });
  }

  logout() {
    this.trackEvent("logout");
  }

  register(data: IAnalyticsEventData) {
    this.trackEvent("register", { ...data });
  }

  error(data: IAnalyticsEventData) {
    this.trackEvent("error", data);
  }

  filterAdvizers(data: IAnalyticsEventData) {
    this.trackEvent("filter_advizers", { context: "Advizer Search", ...data });
  }

  noAdvizersFound(data: IAnalyticsEventData) {
    this.trackEvent("no_advizers_found", { ...data });
  }

  startVideo({
    video,
    ...rest
  }: { video: IAdvizerVideo } & IAnalyticsEventData) {
    console.log({ video });
    this.trackEvent("video_start", { ...this.getVideoParams(video), ...rest });
  }

  videoProgress({
    video,
    ...rest
  }: { video: IAdvizerVideo } & IAnalyticsEventData) {
    this.trackEvent("video_progress", {
      ...this.getVideoParams(video),
      ...rest,
    });
  }

  pauseVideo({
    video,
    ...rest
  }: { video: IAdvizerVideo } & IAnalyticsEventData) {
    this.trackEvent("video_pause", { ...this.getVideoParams(video), ...rest });
  }

  seekVideo({
    video,
    ...rest
  }: { video: IAdvizerVideo } & IAnalyticsEventData) {
    this.trackEvent("video_seek", { ...this.getVideoParams(video), ...rest });
  }

  stopVideo({
    video,
    ...rest
  }: { video: IAdvizerVideo } & IAnalyticsEventData) {
    this.trackEvent("video_stop", { ...this.getVideoParams(video), ...rest });
  }

  // Enhanced cookie methods
  acceptCookies() {
    const { activeUser: activeUserRaw } = useStore.getState().user;
    const activeUser = activeUserRaw ? new User(activeUserRaw) : null;

    this.trackEvent("accept_cookies");

    if (activeUser) {
      this.identifyUser(activeUser);
    }
  }

  rejectCookies() {
    this.trackEvent("reject_cookies");
    this.setAnonymousMode();
  }

  videoError(data: IAnalyticsEventData) {
    this.trackEvent("video_error", data);
  }

  videoProgressEvery5Seconds({
    video,
    ...rest
  }: { video: IAdvizerVideo } & IAnalyticsEventData) {
    this.trackEvent("video_progress_every_5_seconds", {
      ...this.getVideoParams(video),
      ...rest,
    });
  }

  pageNotFound() {
    this.trackEvent("page_404");
  }

  updateUserProfile({
    playlist,
    ...rest
  }: IAnalyticsEventData & { playlist: any }) {
    this.trackEvent("update_user_profile", {
      ...this.getActivePlaylistParams({
        playlist,
      }),
      ...rest,
    });
  }

  filterPlaylists(data: IAnalyticsEventData) {
    this.trackEvent("filter_playlists", data);
  }

  playlistStart({
    playlist,
    ...rest
  }: IAnalyticsEventData & { playlist: any }) {
    this.trackEvent("playlist_start", {
      ...this.getActivePlaylistParams({
        playlist,
      }),
      ...rest,
    });
  }

  playlistComplete({
    playlist,
    ...rest
  }: IAnalyticsEventData & { playlist: any }) {
    this.trackEvent("playlist_complete", {
      ...this.getActivePlaylistParams({
        playlist,
      }),
      ...rest,
    });
  }

  getRecommendedPlaylists(data: IAnalyticsEventData) {
    this.trackEvent("get_recommended_playlists", {
      ...this.getActivePlaylistParams(),
      ...data,
    });
  }

  playlistScaleSelect({
    playlist,
    ...rest
  }: IAnalyticsEventData & { playlist: any }) {
    this.trackEvent("playlist_scale_select", {
      ...this.getActivePlaylistParams({
        playlist,
      }),
      ...rest,
    });
  }

  playlistError({
    playlist,
    ...rest
  }: IAnalyticsEventData & { playlist: any }) {
    this.trackEvent("playlist_error", {
      ...this.getActivePlaylistParams({
        playlist,
      }),
      ...rest,
    });
  }

  getRecommendedAdvizers() {
    this.trackEvent("get_recommended_advizers");
  }

  advizerRecommendationsError(data: IAnalyticsEventData) {
    this.trackEvent("advizer_recommendations_error", data);
  }

  levelUp(data: IAnalyticsEventData) {
    this.trackEvent("level_up", data);
  }

  videoReaction({
    video,
    reaction,
    ...rest
  }: {
    video: IAdvizerVideo;
    reaction: "positive" | "neutral" | "negative";
  } & IAnalyticsEventData) {
    this.trackEvent("video_reaction", {
      ...this.getVideoParams(video),
      action: reaction,
      label: reaction,
      ...rest,
    });
  }

  assignmentCreate(data: IAnalyticsEventData) {
    this.trackEvent("assignment_create", data);
  }

  assignmentUpdateTitle(data: IAnalyticsEventData) {
    this.trackEvent("assignment_update_title", data);
  }

  assignmentUpdateInstructions(data: IAnalyticsEventData) {
    this.trackEvent("assignment_update_instructions", data);
  }

  assignmentUpdateType(data: IAnalyticsEventData) {
    this.trackEvent("assignment_update_type", data);
  }

  assignmentAddPlaylist(data: IAnalyticsEventData) {
    this.trackEvent("assignment_add_playlist", data);
  }

  assignmentRemovePlaylist(data: IAnalyticsEventData) {
    this.trackEvent("assignment_remove_playlist", data);
  }

  assignmentUpdateCount(data: IAnalyticsEventData) {
    this.trackEvent("assignment_update_count", data);
  }

  assignmentUpdateTotalMinutesToComplete(data: IAnalyticsEventData) {
    this.trackEvent("assignment_update_total_minutes_to_complete", data);
  }

  assignmentAddStudent(data: IAnalyticsEventData) {
    this.trackEvent("assignment_add_student", data);
  }

  assignmentUserConsentAccept(data: IAnalyticsEventData) {
    this.trackEvent("assignment_user_consent_accept", data);
  }

  assignmentUserConsentReject(data: IAnalyticsEventData) {
    this.trackEvent("assignment_user_consent_reject", data);
  }

  assignmentAddPrompt(data: IAnalyticsEventData) {
    this.trackEvent("assignment_add_prompt", data);
  }

  assignmentRemovePrompt(data: IAnalyticsEventData) {
    this.trackEvent("assignment_remove_prompt", data);
  }

  assignmentOpenStudentView(data: IAnalyticsEventData) {
    this.trackEvent("assignment_open_student_view", data);
  }

  assignmentOpenResultsView(data: IAnalyticsEventData) {
    this.trackEvent("assignment_open_results_view", data);
  }

  assignmentCopyLink(data: IAnalyticsEventData) {
    this.trackEvent("assignment_copy_link", data);
  }

  assignmentExportStudent(data: IAnalyticsEventData) {
    this.trackEvent("assignment_export_student", data);
  }

  assignmentCopyEmailTemplate(data: IAnalyticsEventData) {
    this.trackEvent("assignment_copy_email_template", data);
  }

  assignmentUpdateStudentResponse(data: IAnalyticsEventData) {
    this.trackEvent("assignment_update_student_response", data);
  }

  assignmentExportAll(data: IAnalyticsEventData) {
    this.trackEvent("assignment_export_all", data);
  }

  assignmentExportCanvas(data: IAnalyticsEventData) {
    this.trackEvent("assignment_export_canvas", data);
  }

  adLinkClick(data: IAnalyticsEventData) {
    this.trackEvent("ad_link_click", data);
  }

  getRecsFromPdf(data: IAnalyticsEventData) {
    this.trackEvent("get_recs_from_pdf", data);
  }

  retryRecsFromPdf(data: IAnalyticsEventData) {
    this.trackEvent("retry_recs_from_pdf", data);
  }

  visitAdvizerVideoDirectly(data: IAnalyticsEventData) {
    this.trackEvent("visit_advizer_video_directly", data);
  }

  updateUserName(data: IAnalyticsEventData) {
    this.trackEvent("user_name_update", data);
  }

  // playlistEvent(data: IAnalyticsEventData) {
  //   this.trackEvent("playlist_event", data);
  // }

  private getActivePlaylistParams(params?: {
    playlist: any;
    stepKey?: string;
  }) {
    const playlist =
      params?.playlist || StoreService.getPlaylistState().activePlaylist;

    if (!playlist) return {};

    const stepKey =
      params?.stepKey || StoreService.getActivePlaylistStepKey() || undefined;

    const playlistRecommendations =
      StoreService.getUserState().playlistData?.recommendedPlaylists || {};

    const playlistStep = stepKey ? playlist.steps?.[stepKey] : undefined;

    return {
      playlistId: playlist.id,
      playlistTitle: playlist.title,
      playlistStepKey: stepKey,
      playlistStepTitle: playlistStep?.title,
      playlistStepType: playlistStep?.stepType,
      isRecommendedPlaylist: !!playlistRecommendations[playlist.id],
    };
  }

  // private getAdvizerParams(advizer: IAdvizer) {
  //   return {
  //     advizerId: advizer.id,
  //     advizerName: advizer.name,
  //   };
  // }

  private getVideoParams(video: IAdvizerVideo) {
    const pdfRecs = JSON.parse(localStorage.getItem("recData") || "{}");
    let isRecommendedAdvizer = false;

    try {
      isRecommendedAdvizer =
        (pdfRecs?.recommendedAdvizers || []).find(
          (advizer: any) => advizer.id === video.advizer?.id,
        ) !== undefined;
    } catch (e) {
      console.error("Error getting recommended advizer", e);
    }

    return {
      ...this.getActivePlaylistParams(),
      videoId: video.id,
      vimeoId: video.videoVimeoId,
      videoUrl: video.url,
      partner: (video.advizer?.partners || [])
        .map((partner) => partner.title)
        .join("|"),
      partnerId: (video.advizer?.partners || [])
        .map((partner) => partner.id)
        .join("|"),
      partnerSlug: (video.advizer?.partners || [])
        .map((partner) => partner.slug)
        .join("|"),
      advizerId: video.advizer?.id,
      advizerName: video.advizer?.name,
      questionNumber: video.questionNumber,
      videoTitle: video.title,
      isRecommendedAdvizer,
    };
  }

  private getAssignmentParams(explicitAssignmentId?: string) {
    const state = useStore.getState();

    // Get the active assignment
    const activeAssignment = state.partnerApp?.fullActiveAssignment;
    const partnerAssignments = state.partnerApp?.assignments || {};

    // Get user assignments
    const userAssignments = state.userAssignments?.userAssignments;
    let assignmentToUse: any = null;

    if (explicitAssignmentId) {
      if (activeAssignment?.id === explicitAssignmentId) {
        assignmentToUse = activeAssignment;
      } else {
        assignmentToUse =
          userAssignments?.[explicitAssignmentId] ||
          partnerAssignments?.[explicitAssignmentId] ||
          null;
      }
    }

    if (!assignmentToUse) {
      const allKnownAssignments = [
        ...Object.values(userAssignments || {}),
        ...Object.values(partnerAssignments || {}),
      ] as any[];
      const mostRecentAssignmentVisited = (allKnownAssignments || [])?.sort(
        (a: any, b: any) => (b?.updatedAt || 0) - (a?.updatedAt || 0),
      )[0];
      assignmentToUse = activeAssignment || mostRecentAssignmentVisited;
    }

    return {
      assignmentId: assignmentToUse?.id,
      assignmentTitle: assignmentToUse?.title,
      partnerSlug: assignmentToUse?.partnerSlug || state.partner?.activePartner?.slug,
    };
  }
}

export const Analytics = new AnalyticsService();
